import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, TextField, Select, MenuItem, Button,
    FormControl, IconButton, CircularProgress,
    Chip, Avatar, Card, CardContent,
    Dialog,
    DialogTitle,
    Tooltip,
    Badge,
    InputLabel,
    OutlinedInput,
    Collapse,
    Autocomplete
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CommentIcon from '@mui/icons-material/Comment';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import * as constants from 'src/utils/constants';
import { useSnackbar } from 'notistack';
import { DialogContent } from '@mui/material';
import { Close, Edit } from '@mui/icons-material';
import UserInfo from 'src/utils/Authorization/UserInfo';
import MediaViewer from 'src/components/MediaViewer';
import axios from 'axios';

export const SupportTickets = () => {
    const userInfo = UserInfo();
    const ref = useRef(null);
    const [tickets, setTickets] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [updatingTickets, setUpdatingTickets] = useState(new Set());
    const [newTicketId, setNewTicketId] = useState(null);
    const [error, setError] = useState('');
    const containerRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const [fileToView, setFileToView] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [selectedTicketComments, setSelectedTicketComments] = useState(null);
    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentLoading, setCommentLoading] = useState(false);
    const [commentFiles, setCommentFiles] = useState([]);

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [filterParams, setFilterParams] = useState({
        status: ['New', 'In Process', 'To Do'],
        priority: [],
        ticket_type: [],
        assignee: []
    });

    useEffect(() => {
        const superAdmin = Array.isArray(userInfo?.role) && userInfo.role.includes('SUPER_ADMIN');
        setIsSuperAdmin(!!superAdmin);
    }, [userInfo])

    const [form, setForm] = useState({
        description: '',
        priority: 'Low',
        ticket_type: 'Bug',
        files: []
    });

    // Priority configurations with colors and flags
    const priorityConfig = {
        Low: { color: '#4caf50', flag: '🟢', chipColor: 'success' },
        Medium: { color: '#ff9800', flag: '🟡', chipColor: 'warning' },
        High: { color: '#f44336', flag: '🔴', chipColor: 'error' },
        Urgent: { color: '#e91e63', flag: '❗', chipColor: 'error' }
    };

    // Status configurations with updated colors
    const statusConfig = {
        new: { color: '#1976d2', chipColor: '#1976d2', label: 'New' },
        to_do: { color: '#0288d1', chipColor: '#FFC300', label: 'To Do' },
        in_process: { color: '#fb8c00', chipColor: '#fb8c00', label: 'In Process' },
        future_feature: { color: '#7b1fa2', chipColor: 'red', label: 'Future Feature' },
        cancelled: { color: '#9e9e9e', chipColor: '#9e9e9e', label: 'Cancelled' },
        done: { color: '#43a047', chipColor: '#43a047', label: 'Done' }
    };

    // Ticket type configurations with updated colors
    const typeConfig = {
        bug: { color: '#e53935', chipColor: 'error', label: 'Bug' },
        new_feature: { color: '#1e88e5', chipColor: 'primary', label: 'New Feature' }
    };

    // Static assignee options (admin-only field)
    const assigneeOptions = ['Yash', 'Rucha', 'Madhav', 'Avinash', 'Het'];

    // Function to append filters to the URL
    const applyFiltersToUrl = (filterParams) => {
        let url = "";
        for (const key in filterParams) {
            if (filterParams[key] && Array.isArray(filterParams[key]) && filterParams[key].length > 0) {
                url += `${key}=${filterParams[key].join(",")}&`;
            }
        }
        return url;
    };

    // Sort tickets by creation date (oldest first for chat-like experience)
    const sortAndGroupTickets = (ticketsList) => {
        return ticketsList.sort((a, b) => {
            return new Date(a.created) - new Date(b.created);
        });
    };

    // Handle filter changes and apply immediately
    const handleFilterChange = (filterType, value) => {
        const newFilterParams = {
            ...filterParams,
            [filterType]: value
        };
        setFilterParams(newFilterParams);

        // Apply filters immediately
        setTickets([]);
        setPage(0);
        setHasMore(true);

        // Update the fetchTickets dependency by using the new filter params directly
        setTimeout(() => {
            fetchTicketsWithFilters(0, true, newFilterParams);
        }, 0);
    };

    // Separate function to fetch tickets with specific filter params
    const fetchTicketsWithFilters = useCallback(async (pageNum = 0, reset = false, filters = filterParams) => {
        if (loading || (!hasMore && !reset)) return;
        setLoading(true);
        setError('');
        try {
            // Get tickets ordered by creation date (oldest first for chat experience)
            let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/support-tickets?page=${pageNum + 1}&page_size=5&ordering=created&`;

            // Append filters to the URL
            apiUrl += applyFiltersToUrl(filters);

            // Remove trailing '&' if present
            apiUrl = apiUrl.replace(/&$/, '');

            const response = await axios.get(apiUrl, {
                headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` }
            });
            console.log("response", response )
            const newTickets = response?.data?.results?.data || [];
            const totalCount = response?.data?.count || 0;

            if (reset) {
                const sortedTickets = sortAndGroupTickets(newTickets);
                setTickets(sortedTickets);
                setPage(0);
                // Scroll to bottom after initial load
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = containerRef.current.scrollHeight;
                    }
                }, 100);
            } else {
                // Store current scroll position
                const scrollElement = containerRef.current;
                const oldScrollHeight = scrollElement?.scrollHeight || 0;
                // Add older tickets at the beginning
                const combinedTickets = [...newTickets, ...tickets];
                const sortedTickets = sortAndGroupTickets(combinedTickets);
                setTickets(sortedTickets);
                setPage(pageNum);
                // Maintain scroll position after adding older messages
                setTimeout(() => {
                    if (scrollElement) {
                        const newScrollHeight = scrollElement.scrollHeight;
                        scrollElement.scrollTop = newScrollHeight - oldScrollHeight;
                    }
                }, 50);
            }
            setHasMore(tickets.length + newTickets.length < totalCount);
        } catch (err) {
            if (err?.response?.data?.message) {
                enqueueSnackbar(err.response.data.message || "Something went wrong. Please try after sometime.", {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "error",
                    autoHideDuration: 2000,
                });
            }
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, [loading, hasMore, tickets.length]);

    const fetchTickets = useCallback(async (pageNum = 0, reset = false) => {
        return fetchTicketsWithFilters(pageNum, reset, filterParams);
    }, [fetchTicketsWithFilters, filterParams]);

    // Clear all filters
    const clearFilters = () => {
        const emptyFilters = {
            status: [],
            priority: [],
            ticket_type: [],
            assignee: []
        };
        setFilterParams(emptyFilters);
        setTickets([]);
        setPage(0);
        setHasMore(true);
        // Apply empty filters immediately
        setTimeout(() => {
            fetchTicketsWithFilters(0, true, emptyFilters);
        }, 0);
    };

    // Check if any filters are active
    const hasActiveFilters = () => {
        return Object.values(filterParams).some(filter => filter.length > 0);
    };

    const handleScroll = useCallback(() => {
        if (!containerRef.current || loading || !hasMore) return;
        const { scrollTop } = containerRef.current;
        // Load more when scrolling to top (for older messages)
        if (scrollTop <= 100) {
            fetchTickets(page + 1);
        }
    }, [fetchTickets, loading, hasMore, page]);

    useEffect(() => {
        fetchTickets(0, true);
    }, []);

    useEffect(() => {
        const ref = containerRef.current;
        if (ref) {
            ref.addEventListener('scroll', handleScroll);
            return () => ref.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        const maxTotalFiles = 5; // Maximum 5 files per ticket
        
        // Check if adding these files would exceed the limit
        if (form.files.length + selectedFiles.length > maxTotalFiles) {
            enqueueSnackbar(`Maximum ${maxTotalFiles} files allowed per ticket`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error",
                autoHideDuration: 3000,
            });
            e.target.value = '';
            return;
        }
        
        // Improved file type validation
        const allowedExtensions = [
            'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
            'pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar'
        ];
        const allowedMimeTypes = [
            'image/', 'application/pdf', 'text/plain',
            'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/zip', 'application/x-zip-compressed', 'application/x-rar-compressed'
        ];
        
        const validFiles = [];
        const invalidFiles = [];
        
        selectedFiles.forEach(file => {
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            const isValidExtension = allowedExtensions.includes(fileExtension);
            const isValidMimeType = allowedMimeTypes.some(type => file.type.startsWith(type));
            
            if (file.size > maxFileSize) {
                invalidFiles.push(`${file.name} (exceeds 10MB limit)`);
            } else if (file.size === 0) {
                invalidFiles.push(`${file.name} (empty file)`);
            } else if (!isValidExtension && !isValidMimeType) {
                invalidFiles.push(`${file.name} (unsupported file type)`);
            } else {
                // Check for duplicate files
                const isDuplicate = form.files.some(existingFile => 
                    existingFile.name === file.name && existingFile.size === file.size
                );
                if (isDuplicate) {
                    invalidFiles.push(`${file.name} (already added)`);
                } else {
                    validFiles.push(file);
                }
            }
        });
        
        if (invalidFiles.length > 0) {
            enqueueSnackbar(`Invalid files: ${invalidFiles.join(', ')}`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error",
                autoHideDuration: 5000,
            });
        }
        
        if (validFiles.length > 0) {
            setForm((prev) => ({ ...prev, files: [...prev.files, ...validFiles] }));
            enqueueSnackbar(`${validFiles.length} file(s) added successfully`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
                autoHideDuration: 2000,
            });
        }
        
        // Reset file input
        e.target.value = '';
    };

    // Handle comment file selection
    const handleCommentFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        const maxTotalFiles = 3; // Maximum 3 files per comment
        
        // Check if adding these files would exceed the limit
        if (commentFiles.length + selectedFiles.length > maxTotalFiles) {
            enqueueSnackbar(`Maximum ${maxTotalFiles} files allowed per comment`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error",
                autoHideDuration: 3000,
            });
            e.target.value = '';
            return;
        }
        
        // Use same validation as main file upload
        const allowedExtensions = [
            'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
            'pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar'
        ];
        const allowedMimeTypes = [
            'image/', 'application/pdf', 'text/plain',
            'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/zip', 'application/x-zip-compressed', 'application/x-rar-compressed'
        ];
        
        const validFiles = [];
        const invalidFiles = [];
        
        selectedFiles.forEach(file => {
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            const isValidExtension = allowedExtensions.includes(fileExtension);
            const isValidMimeType = allowedMimeTypes.some(type => file.type.startsWith(type));
            
            if (file.size > maxFileSize) {
                invalidFiles.push(`${file.name} (exceeds 10MB limit)`);
            } else if (file.size === 0) {
                invalidFiles.push(`${file.name} (empty file)`);
            } else if (!isValidExtension && !isValidMimeType) {
                invalidFiles.push(`${file.name} (unsupported file type)`);
            } else {
                // Check for duplicate files
                const isDuplicate = commentFiles.some(existingFile => 
                    existingFile.name === file.name && existingFile.size === file.size
                );
                if (isDuplicate) {
                    invalidFiles.push(`${file.name} (already added)`);
                } else {
                    validFiles.push(file);
                }
            }
        });
        
        if (invalidFiles.length > 0) {
            enqueueSnackbar(`Invalid files: ${invalidFiles.join(', ')}`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error",
                autoHideDuration: 5000,
            });
        }
        
        if (validFiles.length > 0) {
            setCommentFiles(prev => [...prev, ...validFiles]);
            enqueueSnackbar(`${validFiles.length} file(s) added to comment`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
                autoHideDuration: 2000,
            });
        }
        
        e.target.value = '';
    };

    // Handle removing individual files
    const handleRemoveFile = (indexToRemove) => {
        setForm((prev) => ({
            ...prev,
            files: prev.files.filter((_, index) => index !== indexToRemove)
        }));
    };

    // Handle removing comment files
    const handleRemoveCommentFile = (indexToRemove) => {
        setCommentFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = async () => {
        if (!form.description.trim()) {
            enqueueSnackbar('Please describe your issue', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error",
                autoHideDuration: 3000,
            });
            return;
        }

        let requestData;
        let headers = {
            Authorization: `Token ${localStorage.getItem('authToken')}`
        };
        
        if (form.files.length > 0) {
            requestData = new FormData();
            requestData.append('description', form.description.trim());
            requestData.append('priority', form.priority);
            requestData.append('ticket_type', form.ticket_type);
            
            // Append files with proper naming
            form.files.forEach((file, index) => {
                requestData.append('files', file, file.name);
            });
            
            // Let browser set Content-Type with boundary for FormData
            // Don't manually set Content-Type header
        } else {
            requestData = {
                description: form.description.trim(),
                priority: form.priority,
                ticket_type: form.ticket_type
            };
            headers['Content-Type'] = 'application/json';
        }

        try {
            setLoading(true);
            if (form.editingId) {
                const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/support-tickets/${form.editingId}/`;
                await axios.patch(apiUrl, requestData, { headers });
                
                setTickets(prevTickets =>
                    prevTickets.map(ticket =>
                        ticket.id === form.editingId
                            ? { ...ticket, description: form.description.trim(), priority: form.priority, ticket_type: form.ticket_type }
                            : ticket
                    )
                );
                enqueueSnackbar('Ticket updated successfully!', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "success",
                    autoHideDuration: 3000,
                });
            } else {
                const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/support-tickets/`;
                const response = await axios.post(apiUrl, requestData, { headers });
                
                setNewTicketId(response.data.id);
                
                enqueueSnackbar('Ticket created successfully!', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "success",
                    autoHideDuration: 3000,
                });
                
                await fetchTickets(0, true);
                
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = containerRef.current.scrollHeight;
                    }
                    setNewTicketId(null);
                }, 600);
            }
            
            setForm({ description: '', priority: 'Low', ticket_type: 'Bug', files: [] });
            const fileInput = document.getElementById('file-input');
            if (fileInput) fileInput.value = '';
        } catch (err) {
            console.error(`Failed to ${form.editingId ? 'update' : 'create'} ticket:`, err);
            
            // Enhanced error handling for file upload issues
            if (err.response?.status === 413) {
                enqueueSnackbar('File size too large. Please reduce file sizes and try again.', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "error",
                    autoHideDuration: 4000,
                });
            } else if (err.response?.status === 415) {
                enqueueSnackbar('Unsupported file type. Please check file formats and try again.', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "error",
                    autoHideDuration: 4000,
                });
            } else if (err.response?.status === 400) {
                const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Invalid request. Please check your input.';
                enqueueSnackbar(errorMessage, {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "error",
                    autoHideDuration: 4000,
                });
            } else {
                const errorMessage = err.response?.data?.message || err.response?.data?.error || `Failed to ${form.editingId ? 'update' : 'create'} ticket. Please try again.`;
                enqueueSnackbar(errorMessage, {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "error",
                    autoHideDuration: 4000,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTicketUpdate = async (ticketId, field, value) => {
        try {
            setUpdatingTickets(prev => new Set([...prev, ticketId]));
            
            let valueToSend = value;
            if (field === 'status') {
                valueToSend = statusConfig[value]?.label || value;
            }

            await axios.patch(
                `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/support-tickets/${ticketId}/`,
                { [field]: valueToSend },
                {
                    headers: {
                        Authorization: `Token ${localStorage.getItem('authToken')}`
                    }
                }
            );
            
            setTickets((prev) =>
                prev.map((ticket) =>
                    ticket.id === ticketId ? { ...ticket, [field]: valueToSend } : ticket
                )
            );
            
            setTimeout(() => {
                setUpdatingTickets(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(ticketId);
                    return newSet;
                });
            }, 800);
            
            enqueueSnackbar(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully.`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: 'success',
                autoHideDuration: 2000,
            });
        } catch (error) {
            setUpdatingTickets(prev => {
                const newSet = new Set(prev);
                newSet.delete(ticketId);
                return newSet;
            });
            
            enqueueSnackbar(`Failed to update ${field}.`, {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: 'error',
                autoHideDuration: 2000,
            });
        }
    };

    // Fetch comments for a specific ticket
    const fetchTicketComments = async (ticketId) => {
        try {
            const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/ticket-comments/?ticket_id=${ticketId}`;
            
            const response = await axios.get(apiUrl, {
                headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` }
            });
            
            return response.data?.data || [];
        } catch (error) {
            console.error('Failed to fetch comments:', error);
            return [];
        }
    };

    // Handle viewing comments
    const handleViewComments = async (ticket) => {
        setCommentLoading(true);
        setCommentDialogOpen(true);
        
        // Clear previous comments immediately
        setSelectedTicketComments({
            ticket: ticket,
            comments: []
        });
        
        try {
            const comments = await fetchTicketComments(ticket.id);
            setSelectedTicketComments({
                ticket: ticket,
                comments: comments
            });
        } catch (error) {
            setSelectedTicketComments({
                ticket: ticket,
                comments: []
            });
        } finally {
            setCommentLoading(false);
        }
    };

    // Handle adding a comment
    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            setCommentLoading(true);
            const headers = {
                "Authorization": `Token ${localStorage.getItem('authToken')}`
            };
            
            let requestData;
            let commentHeaders = {
                "Authorization": `Token ${localStorage.getItem('authToken')}`
            };
            
            if (commentFiles.length > 0) {
                requestData = new FormData();
                requestData.append('comment', newComment.trim());
                requestData.append('ticket_id', selectedTicketComments.ticket.id);
                
                // Append files with proper naming
                commentFiles.forEach((file, index) => {
                    requestData.append('files', file, file.name);
                });
                
                // Let browser set Content-Type with boundary for FormData
            } else {
                requestData = {
                    comment: newComment.trim(),
                    ticket_id: selectedTicketComments.ticket.id
                };
                commentHeaders['Content-Type'] = 'application/json';
            }
            
            const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/ticket-comments/`;
            const response = await axios.post(apiUrl, requestData, { headers: commentHeaders });
            
            // Refresh comments to get updated list with attachments
            const updatedComments = await fetchTicketComments(selectedTicketComments.ticket.id);
            setSelectedTicketComments(prev => ({
                ...prev,
                comments: updatedComments
            }));
            
            // Update the ticket in the main list to show it has comments
            setTickets(prev => prev.map(ticket =>
                ticket.id === selectedTicketComments.ticket.id
                    ? { ...ticket, comments_count: (ticket.comments_count || 0) + 1 }
                    : ticket
            ));
            
            setNewComment('');
            setCommentFiles([]);
            
            // Reset comment file input
            const commentFileInput = document.getElementById('comment-file-input');
            if (commentFileInput) commentFileInput.value = '';
            
            enqueueSnackbar('Comment added successfully!', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: 'success',
                autoHideDuration: 2000,
            });
        } catch (error) {
            console.error('Failed to add comment:', error);
            
            // Enhanced error handling for comment file uploads
            if (error.response?.status === 413) {
                enqueueSnackbar('File size too large. Please reduce file sizes and try again.', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: 'error',
                    autoHideDuration: 4000,
                });
            } else if (error.response?.status === 415) {
                enqueueSnackbar('Unsupported file type. Please check file formats and try again.', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: 'error',
                    autoHideDuration: 4000,
                });
            } else if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Invalid request. Please check your input.';
                enqueueSnackbar(errorMessage, {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: 'error',
                    autoHideDuration: 4000,
                });
            } else {
                const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to add comment.';
                enqueueSnackbar(errorMessage, {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        } finally {
            setCommentLoading(false);
        }
    };

    // Check if file is an image
    const isImageFile = (filename) => {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const extension = filename.split('.').pop()?.toLowerCase();
        return imageExtensions.includes(extension);
    };

    // Get file name from path
    const getFileName = (filePath) => {
        return filePath.split('/').pop();
    };

    // Handle attachment click
    const handleAttachmentClick = (attachment) => {
        const fileName = getFileName(attachment.file);
        // Format the file object for MediaViewer
        const fileForViewer = {
            id: attachment.id,
            name: fileName,
            fileUrl: attachment.file,
        };
        setFileToView(fileForViewer);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown Date';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        const htmlElement = document.documentElement;
        htmlElement.style.overflow = 'hidden';
        return () => {
            htmlElement.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        const parent = ref.current?.parentElement;
        if (parent) {
            parent.style.paddingBottom = '0';
            parent.style.height = '100vh';
        }
    }, []);

    return (
        <Box ref={ref} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden', minWidth: 0 }}>
            {/* Filter Section */}
            <Box sx={{
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fff',
                px: { xs: 1, sm: 2},
                py: 1,
                flexShrink: 0
            }}>
                {/* Filter Toggle */}
                <Box sx={{
                    borderBottom: '1px solid #e0e0e0',
                    borderRadius: '16px',
                    backgroundColor: '#fff',
                    px: { xs: 1, sm: 2},
                    py: 1.5,
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(10px)',
                }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterListIcon sx={{ color: hasActiveFilters() ? '#1976d2' : '#666' }} />
                                <Typography variant="body2" sx={{ color: hasActiveFilters() ? '#1976d2' : '#666' }}>
                                    Filters {hasActiveFilters() && `(${Object.values(filterParams).reduce((acc, curr) => acc + curr.length, 0)})`}
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                size="small"
                                options={Object.keys(statusConfig)}           // ["new", "to_do", ...]
                                value={filterParams.status}                   // still array of strings
                                onChange={(_, newValue) => handleFilterChange('status', newValue)}
                                disableCloseOnSelect
                                getOptionLabel={(option) =>
                                    statusConfig[option]?.label || option
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                    {statusConfig[option]?.label || option}
                                    </li>
                                )}
                                renderTags={(selected, getTagProps) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value, index) => (
                                        <Chip
                                        key={value}
                                        label={statusConfig[value]?.label || value}
                                        size="small"
                                        {...getTagProps({ index })}
                                        />
                                    ))}
                                    </Box>
                                )}
                                sx={{ minWidth: 220 }}
                                renderInput={(params) => (
                                    <TextField
                                    {...params}
                                    label="Status"
                                    placeholder="Select status"
                                    />
                                )}
                            />

                            <Autocomplete
                                multiple
                                size="small"
                                options={Object.keys(priorityConfig)}   // ["Low","Medium","High","Urgent"]
                                value={filterParams.priority}
                                onChange={(_, newValue) => handleFilterChange('priority', newValue)}
                                disableCloseOnSelect
                                getOptionLabel={(option) =>
                                    `${priorityConfig[option]?.flag || ''} ${option}`
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                    {priorityConfig[option]?.flag} {option}
                                    </li>
                                )}
                                renderTags={(selected, getTagProps) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value, index) => (
                                        <Chip
                                        key={value}
                                        label={`${priorityConfig[value]?.flag || ''} ${value}`}
                                        size="small"
                                        {...getTagProps({ index })}
                                        />
                                    ))}
                                    </Box>
                                )}
                                sx={{ minWidth: 220 }}
                                renderInput={(params) => (
                                    <TextField
                                    {...params}
                                    label="Priority"
                                    placeholder="Select priority"
                                    />
                                )}
                            />

                            <Autocomplete
                                multiple
                                size="small"
                                options={Object.keys(typeConfig)}   // ["bug","new_feature"]
                                value={filterParams.ticket_type}
                                onChange={(_, newValue) => handleFilterChange('ticket_type', newValue)}
                                disableCloseOnSelect
                                getOptionLabel={(option) =>
                                    typeConfig[option]?.label || option
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                    {typeConfig[option]?.label || option}
                                    </li>
                                )}
                                renderTags={(selected, getTagProps) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value, index) => (
                                        <Chip
                                        key={value}
                                        label={typeConfig[value]?.label || value}
                                        size="small"
                                        {...getTagProps({ index })}
                                        />
                                    ))}
                                    </Box>
                                )}
                                sx={{ minWidth: 220 }}
                                renderInput={(params) => (
                                    <TextField
                                    {...params}
                                    label="Type"
                                    placeholder="Select type"
                                    />
                                )}
                            />

                            {isSuperAdmin && (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={assigneeOptions}
                                    value={filterParams.assignee}
                                    onChange={(_, newValue) => handleFilterChange('assignee', newValue)}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                        {option}
                                        </li>
                                    )}
                                    renderTags={(selected, getTagProps) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value, index) => (
                                            <Chip
                                            key={value}
                                            label={value}
                                            size="small"
                                            {...getTagProps({ index })}
                                            />
                                        ))}
                                        </Box>
                                    )}
                                    sx={{ minWidth: 220 }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        label="Assignee"
                                        placeholder="Select assignee"
                                        />
                                    )}
                                />
                            )}

                        </Box>
                        {hasActiveFilters() && (
                            <Button
                                size="small"
                                onClick={clearFilters}
                                startIcon={<ClearIcon />}
                                sx={{ 
                                    color: '#666',
                                    borderRadius: '20px',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                        color: '#f44336',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>
                </Box>
                {/* Filter Controls */}
                <Collapse in={showFilters}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        {/* Status Filter */}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                multiple
                                value={filterParams.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                input={<OutlinedInput label="Status" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip
                                                key={value}
                                                label={statusConfig[value]?.label || value}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {Object.entries(statusConfig).map(([status, config]) => (
                                    <MenuItem key={status} value={status}>
                                        {config.label || status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Priority Filter */}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                multiple
                                value={filterParams.priority}
                                onChange={(e) => handleFilterChange('priority', e.target.value)}
                                input={<OutlinedInput label="Priority" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip
                                                key={value}
                                                label={`${priorityConfig[value]?.flag} ${value}`}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {Object.entries(priorityConfig).map(([priority, config]) => (
                                    <MenuItem key={priority} value={priority}>
                                        {config.flag} {priority}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Type Filter */}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                multiple
                                value={filterParams.ticket_type}
                                onChange={(e) => handleFilterChange('ticket_type', e.target.value)}
                                input={<OutlinedInput label="Type" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={typeConfig[value]?.label || value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {Object.entries(typeConfig).map(([type, config]) => (
                                    <MenuItem key={type} value={type}>
                                        {config.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Apply Filters Button - Removed since filters apply automatically */}
                    </Box>
                </Collapse>
            </Box>

            {/* Tickets List - Scrollable */}
            <Box
                ref={containerRef}
                sx={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    backgroundColor: '#fafafa',
                    px: { xs: 0.5, sm: 2 },
                    py: 2,
                    minHeight: 0,
                    position: 'relative',
                    pb: { xs: 10, sm: 12 }
                }}
            >
                {initialLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : tickets.length === 0 ? (
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {hasActiveFilters() ? 'No tickets match your filters' : 'No tickets found'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {hasActiveFilters() ? 'Try adjusting your filter criteria' : 'Create your first support ticket below'}
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Loading indicator at top for older messages */}
                        {loading && !initialLoading && (
                            <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ ml: 1, color: '#666' }}>
                                    Loading older messages...
                                </Typography>
                            </Box>
                        )}
                        {tickets.map((ticket, index) => {
                            const currentDate = new Date(ticket.created).toDateString();
                            const previousDate = index > 0 ? new Date(tickets[index - 1].created).toDateString() : null;
                            const showDateSeparator = currentDate !== previousDate;

                            const matchedtypeConfig = Object.values(typeConfig).find(
                                (config) => config.label === ticket.ticket_type
                            );
                            return (
                                <React.Fragment key={`${ticket.id}-${index}`}>
                                    {/* Date Separator */}
                                    {showDateSeparator && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: index > 0 ? 2 : 0 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    backgroundColor: '#e0e0e0',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '12px',
                                                    color: '#666',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {new Date(ticket.created).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                }).toUpperCase()}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Ticket Message */}
                                    <Box sx={{ 
                                        mb: 3,
                                        animation: newTicketId === ticket.id
                                            ? 'slideInFromBottom 0.6s ease-out' 
                                            : updatingTickets.has(ticket.id)
                                            ? 'pulseUpdate 0.8s ease-in-out'
                                            : 'none'
                                    }}>
                                        {/* User Info */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Avatar sx={{ 
                                                width: 36, 
                                                height: 36, 
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: '#fff',
                                                fontSize: '16px',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                            }}>
                                                {ticket.created_by && (ticket.created_by.first_name || ticket.created_by.last_name) ? 
                                                    `${ticket.created_by.first_name?.[0] || ''}${ticket.created_by.last_name?.[0] || ''}` :
                                                    '👤'
                                                }
                                            </Avatar>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                {ticket.created_by ?
                                                    `${ticket.created_by.first_name || ''} ${ticket.created_by.last_name || ''}`.trim() || 'Unknown User' :
                                                    `User #${ticket.id}`
                                                }
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                {ticket.created ? new Date(ticket.created).toLocaleTimeString('en-US', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false
                                                }) : 'Unknown time'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                <Chip
                                                    label={ticket.ticket_type || 'Unknown'}
                                                    sx={{ color: matchedtypeConfig?.color || '#666' }}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Typography>
                                            {isSuperAdmin && (
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                    {ticket.assignee ? `Assignee: ${ticket.assignee}` : 'Unassigned'}
                                                </Typography>
                                            )}
                                            {ticket.created_by?.username === userInfo?.username && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setForm({
                                                        description: ticket.description,
                                                        priority: ticket.priority,
                                                        ticket_type: ticket.ticket_type,
                                                        files: [],
                                                        editingId: ticket.id
                                                    })}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            )}
                                            {/* Admin Comment Controls */}
                                            <Box sx={{ display: 'flex', gap: 0.5, }}>
                                                {/* Comment Icon with Badge */}
                                                <Tooltip title={`Comments${ticket.comments_count ? ` (${ticket.comments_count})` : ''}`}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewComments(ticket)}
                                                        sx={{ color: '#1976d2' }}
                                                    >
                                                        <Badge
                                                            badgeContent={ticket.comments_count || 0}
                                                            color="primary"
                                                            invisible={!ticket.comments_count}
                                                        >
                                                            <CommentIcon fontSize="small" />
                                                        </Badge>
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                        {/* Message Content */}
                                        <Box sx={{
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            borderRadius: 3,
                                            p: 2.5,
                                            ml: 5,
                                            position: 'relative',
                                            maxWidth: '75%',
                                            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                                            border: '1px solid rgba(0, 0, 0, 0.06)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)'
                                            }
                                        }}>
                                            <Typography style={{ whiteSpace: "pre-line" }}>
                                                {ticket.description || 'No description provided'}
                                            </Typography>
                                            {/* Right side status indicators */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    mt: 1,
                                                    alignItems: 'flex-start',
                                                    flexWrap: 'wrap',
                                                    gap: 2
                                                }}
                                            >
                                                {/* Attachments on the left */}
                                                {ticket.attachments && ticket.attachments.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {ticket.attachments.map((attachment, i) => (
                                                            <Chip
                                                                key={`${attachment.id}-${i}`}
                                                                icon={isImageFile(attachment.file) ? <ImageIcon /> : <InsertDriveFileIcon />}
                                                                label={getFileName(attachment.file)}
                                                                onClick={() => handleAttachmentClick(attachment)}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    maxWidth: '200px',
                                                                    cursor: 'pointer',
                                                                    '& .MuiChip-label': {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : <div></div>}
                                                {/* Priority and Status on the right */}
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                    {isSuperAdmin ? (
                                                        <FormControl variant="standard" sx={{ minWidth: 80 }}>
                                                            <Select
                                                                value={ticket.status}
                                                                onChange={(e) => handleTicketUpdate(ticket.id, 'status', e.target.value)}
                                                                disableUnderline
                                                                displayEmpty
                                                                renderValue={(selected) => {
                                                                    const matchedStatusConfig = Object.values(statusConfig).find(
                                                                        (config) => config.label === selected
                                                                    ) || { label: selected, chipColor: '#e0e0e0' };
                                                                    return (
                                                                        <Chip
                                                                            label={matchedStatusConfig.label || selected}
                                                                            size="small"
                                                                            variant="filled"
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                backgroundColor: matchedStatusConfig.chipColor || '#e0e0e0',
                                                                                color: '#fff',
                                                                                borderRadius: '16px'
                                                                            }}
                                                                        />
                                                                    );
                                                                }}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            mt: 1,
                                                                            borderRadius: 1,
                                                                        },
                                                                    },
                                                                }}
                                                                sx={{
                                                                    '& .MuiSelect-select': { padding: 0 },
                                                                }}
                                                            >
                                                                {Object.entries(statusConfig).map(([status, config]) => (
                                                                    <MenuItem key={status} value={status}>
                                                                        {config.label || status}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Chip
                                                            label={statusConfig[ticket.status]?.label || ticket.status}
                                                            size="small"
                                                            variant="filled"
                                                            sx={{
                                                                backgroundColor: statusConfig[ticket.status]?.chipColor || '#e0e0e0',
                                                                color: '#fff',
                                                                borderRadius: '16px'
                                                            }}
                                                        />
                                                    )}
                                                    {/* Admin-only assignee control */}
                                                    {isSuperAdmin && (
                                                        <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                                            <Select
                                                                value={ticket.assignee || ''}
                                                                onChange={(e) => handleTicketUpdate(ticket.id, 'assignee', e.target.value || null)}
                                                                disableUnderline
                                                                displayEmpty
                                                                renderValue={(selected) => (
                                                                    <Typography variant="caption" sx={{ color: '#555' }}>
                                                                        {selected ? `Assignee: ${selected}` : 'Assign owner'}
                                                                    </Typography>
                                                                )}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            mt: 1,
                                                                            borderRadius: 1,
                                                                        },
                                                                    },
                                                                }}
                                                                sx={{
                                                                    '& .MuiSelect-select': { padding: 0 },
                                                                }}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Unassigned</em>
                                                                </MenuItem>
                                                                {assigneeOptions.map((name) => (
                                                                    <MenuItem key={name} value={name}>
                                                                        {name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    {isSuperAdmin ? (
                                                        <FormControl variant="standard" sx={{ minWidth: 80 }}>
                                                            <Select
                                                                value={ticket.priority}
                                                                onChange={(e) => handleTicketUpdate(ticket.id, 'priority', e.target.value)}
                                                                disableUnderline
                                                                displayEmpty
                                                                renderValue={(selected) => (
                                                                    <Chip
                                                                        label={priorityConfig[selected]?.flag}
                                                                        color="default"
                                                                        size="small"
                                                                        sx={{ cursor: 'pointer' }}
                                                                    />
                                                                )}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            mt: 1,
                                                                            borderRadius: 1,
                                                                        },
                                                                    },
                                                                }}
                                                                sx={{
                                                                    '& .MuiSelect-select': { padding: 0 },
                                                                    '& .MuiChip-root': { borderRadius: '16px' }
                                                                }}
                                                            >
                                                                {Object.entries(priorityConfig).map(([priority, config]) => (
                                                                    <MenuItem key={priority} value={priority}>
                                                                        {config.flag} {priority}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Tooltip title={ticket.priority}>
                                                            <Chip
                                                                label={priorityConfig[ticket.priority]?.flag}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#f0f0f0',
                                                                    color: 'white',
                                                                    minWidth: '40px'
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                    </>
                )}
                {loading && !initialLoading && (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>

            {/* Comments Dialog */}
            <Dialog
                open={commentDialogOpen}
                onClose={() => {
                    setCommentDialogOpen(false);
                    setNewComment('');
                    setCommentFiles([]);
                    setSelectedTicketComments(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Comments for Ticket #{selectedTicketComments?.ticket?.id}
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setCommentDialogOpen(false);
                            setNewComment('');
                            setCommentFiles([]);
                            setSelectedTicketComments(null); // Clear the selected ticket comments
                        }}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    {/* Ticket Info */}
                    <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Description:</strong> {selectedTicketComments?.ticket?.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            <strong>Status:</strong> {selectedTicketComments?.ticket?.status} |
                            <strong> Priority:</strong> {selectedTicketComments?.ticket?.priority} |
                            <strong> Type:</strong> {selectedTicketComments?.ticket?.ticket_type}
                        </Typography>
                    </Box>
                    {/* Comments List */}
                    <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 2 }}>
                        {commentLoading ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Loading comments...
                                </Typography>
                            </Box>
                        ) : selectedTicketComments?.comments?.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No comments yet for Ticket #{selectedTicketComments?.ticket?.id}
                                </Typography>
                            </Box>
                        ) : (
                            selectedTicketComments?.comments?.map((comment, index) => (
                                <Box key={`comment-${comment.id}-${selectedTicketComments.ticket.id}`} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {comment.modified_by
                                                ? `${comment.modified_by.first_name} ${comment.modified_by.last_name}`.trim()
                                                : 'Unknown User'
                                            }
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(comment.created)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {comment.comment}
                                    </Typography>
                                    {/* Comment Attachments */}
                                    {comment.attachments && comment.attachments.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                            {comment.attachments.map((attachment, i) => (
                                                <Chip
                                                    key={`comment-${comment.id}-attachment-${i}`}
                                                    icon={isImageFile(attachment.file) ? <ImageIcon /> : <InsertDriveFileIcon />}
                                                    label={getFileName(attachment.file)}
                                                    onClick={() => handleAttachmentClick(attachment)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        maxWidth: '200px',
                                                        cursor: 'pointer',
                                                        '& .MuiChip-label': {
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))
                        )}
                    </Box>
                    {/* Add Comment Section */}
                    <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Add Comment
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Type your comment here..."
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                        />
                        {/* Comment File Upload */}
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.pdf,.txt,.doc,.docx,.xls,.xlsx,.zip,.rar"
                                type="file"
                                multiple
                                hidden
                                id="comment-file-input"
                                onChange={handleCommentFileChange}
                            />
                            <label htmlFor="comment-file-input">
                                <IconButton
                                    size="small"
                                    component="span"
                                    sx={{ color: '#2e7d32' }}
                                    disabled={commentFiles.length >= 3}
                                >
                                    <AttachFileIcon />
                                </IconButton>
                            </label>
                            <Typography variant="caption" color="text.secondary">
                                Attach files (max 3 files, 10MB each)
                            </Typography>
                            {commentFiles.length >= 3 && (
                                <Typography variant="caption" color="error">
                                    Maximum files reached
                                </Typography>
                            )}
                        </Box>
                        {/* Comment File Display */}
                        {commentFiles.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {commentFiles.map((file, index) => (
                                    <Chip
                                        key={`comment-file-${index}`}
                                        icon={isImageFile(file.name) ? <ImageIcon /> : <InsertDriveFileIcon />}
                                        label={file.name}
                                        onDelete={() => handleRemoveCommentFile(index)}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                            maxWidth: '200px',
                                            '& .MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setNewComment('');
                                    setCommentFiles([]);
                                    const commentFileInput = document.getElementById('comment-file-input');
                                    if (commentFileInput) commentFileInput.value = '';
                                }}
                                disabled={commentLoading}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAddComment}
                                disabled={!newComment.trim() || commentLoading}
                                startIcon={commentLoading ? <CircularProgress size={16} /> : <SendIcon />}
                            >
                                {commentLoading ? 'Adding...' : 'Add Comment'}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* MediaViewer for attachment preview */}
            {fileToView && (
                <Dialog open={fileToView} maxWidth={false} sx={{ '& .MuiDialog-paper': { width: '80%', maxWidth: '1000px' } }}>
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                        File Viewer
                        <IconButton
                            aria-label="close"
                            onClick={() => setFileToView(null)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <MediaViewer
                            file={{
                                ...fileToView,
                                fileUrl: '/' + fileToView.fileUrl
                            }}
                            onClose={() => setFileToView(null)}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {/* Sticky Input Form */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    zIndex: 10,
                    boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.08)',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                    flexShrink: 0,
                    backdropFilter: 'blur(10px)'
                }}
            >
                {/* Edit Mode Indicator */}
                {form.editingId && (
                    <Box sx={{ 
                        mb: 2, 
                        p: 1.5, 
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        borderRadius: 2,
                        border: '1px solid #2196f3',
                        animation: 'pulse 2s infinite'
                    }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                            ✏️ Editing Ticket #{form.editingId}
                        </Typography>
                    </Box>
                )}
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    {/* Type Dropdown */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            name="ticket_type"
                            value={form.ticket_type}
                            onChange={handleInputChange}
                            displayEmpty
                            sx={{
                                borderRadius: 3,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.12)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#2e7d32'
                                }
                            }}
                        >
                            <MenuItem value="Bug">
                                <Box display="flex" alignItems="center" gap={1}>
                                    🐛 Bug
                                </Box>
                            </MenuItem>
                            <MenuItem value="New Feature">
                                <Box display="flex" alignItems="center" gap={1}>
                                    ✨ Feature
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    {/* Priority Dropdown */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            name="priority"
                            value={form.priority}
                            onChange={handleInputChange}
                            displayEmpty
                            sx={{
                                borderRadius: 3,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.12)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#2e7d32'
                                }
                            }}
                        >
                            {Object.entries(priorityConfig).map(([priority, config]) => (
                                <MenuItem key={priority} value={priority}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{config.flag}</span> {priority}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {/* Description Input */}
                    <TextField
                        name="description"
                        value={form.description}
                        onChange={handleInputChange}
                        placeholder={form.editingId ? "Update your issue..." : "💬 Describe your issue..."}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        maxRows={4}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 4,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                },
                                '&.Mui-focused': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 20px rgba(46, 125, 50, 0.2)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#2e7d32',
                                    borderWidth: 2
                                }
                            }
                        }}
                    />
                    {/* Attach File - Only show for new tickets */}
                    {!form.editingId && (
                        <>
                            <input
                                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.pdf,.txt,.doc,.docx,.xls,.xlsx,.zip,.rar"
                                type="file"
                                multiple
                                hidden
                                id="file-input"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="file-input">
                                <Tooltip title={`📎 Attach files (${form.files.length}/5)`} arrow>
                                    <IconButton
                                        size="medium"
                                        component="span"
                                        sx={{ 
                                            color: form.files.length >= 5 ? '#ccc' : '#2e7d32',
                                            cursor: form.files.length >= 5 ? 'not-allowed' : 'pointer',
                                            backgroundColor: 'rgba(46, 125, 50, 0.08)',
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: form.files.length >= 5 ? 'rgba(46, 125, 50, 0.08)' : 'rgba(46, 125, 50, 0.15)',
                                                transform: form.files.length >= 5 ? 'none' : 'scale(1.1)'
                                            }
                                        }}
                                        disabled={form.files.length >= 5}
                                    >
                                        <AttachFileIcon />
                                    </IconButton>
                                </Tooltip>
                            </label>
                        </>
                    )}
                    {/* Cancel Button - Only show in edit mode */}
                    {form.editingId && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setForm({ description: '', priority: 'Low', ticket_type: 'Bug', files: [] })}
                            sx={{ 
                                minWidth: 'auto',
                                borderRadius: 3,
                                px: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                    {/* Submit Button */}
                    <IconButton
                        onClick={handleSubmit}
                        disabled={!form.description.trim() || loading}
                        sx={{ 
                            color: form.editingId ? '#1976d2' : '#2e7d32',
                            backgroundColor: form.editingId ? 'rgba(25, 118, 210, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: form.editingId ? 'rgba(25, 118, 210, 0.15)' : 'rgba(46, 125, 50, 0.15)',
                                transform: 'scale(1.1)'
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                        title={form.editingId ? 'Update Ticket' : 'Create Ticket'}
                    >
                        {loading ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                </Box>
                {/* File attachments display - Only for new tickets */}
                {!form.editingId && form.files.length > 0 && (
                    <Box sx={{ 
                        mt: 2, 
                        p: 2,
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                        borderRadius: 2,
                        border: '1px dashed rgba(46, 125, 50, 0.3)',
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1.5
                    }}>
                        <Typography variant="caption" sx={{ width: '100%', color: '#2e7d32', fontWeight: 600, mb: 1 }}>
                            📎 Attached Files ({form.files.length}/5)
                        </Typography>
                        {form.files.map((file, index) => (
                            <Chip
                                key={`${file.name}-${index}`}
                                icon={isImageFile(file.name) ? <ImageIcon /> : <InsertDriveFileIcon />}
                                label={file.name}
                                onDelete={() => handleRemoveFile(index)}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{
                                    maxWidth: '200px',
                                    cursor: 'pointer',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                    },
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
